import { useMemo, useState } from 'react';

import { PartnerType, PricingType } from '@prisma/client';
import { useField } from 'formik';
import { PartnerOffers } from 'types/graphql';

import { AssignToggle } from 'src/components';
import Loader from 'src/components/Loader/Loader';
import { useAddPartnerContext } from 'src/contexts/AddPartnerProvider';
import { useTransactionPricing } from 'src/hooks/useTransactionPricing';

import OfferPreviewDialog from '../OfferPreviewDialog';
import OffersContainer from '../OffersContainer/OffersContainer';

export type ActiveTransactionOfferNameIds = Partial<
    {
        offerName?: string;
        id?: string | number;
    }[]
>;

const TransactionsOffers = () => {
    const [selectedOffer, setSelectedOffer] = useState<PartnerOffers>();
    const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
    const [{ value }, _, { setValue }] = useField('assignedTransactionOffers');
    const { application } = useAddPartnerContext();

    const {
        activeTransactionPricingOffers,
        fetchingActiveTransactionOffers,
        activeTransactionPricingOffersForPartner,
        fetchingActiveTransactionOffersForPartner,
    } = useTransactionPricing(application?.partnerId);

    const assignedTransactionOffers: ActiveTransactionOfferNameIds = useMemo(() => {
        // Only get active transaction pricing offers assigned to top-level partner when creating POP
        const activeTransactionOffers: ActiveTransactionOfferNameIds =
            application?.partnerId && application?.partnerType === PartnerType.PARTNER_OF_PARTNER
                ? activeTransactionPricingOffersForPartner || []
                : activeTransactionPricingOffers || [];

        const assignedOfferIds = value.map(({ value }) => value) || [];

        return activeTransactionOffers.filter((transactionOffer) =>
            assignedOfferIds.includes(transactionOffer.id)
        );
    }, [
        activeTransactionPricingOffers,
        activeTransactionPricingOffersForPartner,
        application?.partnerId,
        application?.partnerType,
        value,
    ]);

    const availableTransactionOffers: ActiveTransactionOfferNameIds = useMemo(() => {
        // Only get active transaction pricing offers assigned to top-level partner when creating POP
        const activeTransactionOffers: ActiveTransactionOfferNameIds =
            application?.partnerId && application?.partnerType === PartnerType.PARTNER_OF_PARTNER
                ? activeTransactionPricingOffersForPartner || []
                : activeTransactionPricingOffers || [];
        const assignedTransactionOfferIds = assignedTransactionOffers.map(({ id }) => id);

        return activeTransactionOffers.filter(
            (transactionOffer) => !assignedTransactionOfferIds.includes(transactionOffer.id)
        );
    }, [
        activeTransactionPricingOffers,
        activeTransactionPricingOffersForPartner,
        application?.partnerId,
        application?.partnerType,
        assignedTransactionOffers,
    ]);

    const assigned = useMemo(() => {
        return (assignedTransactionOffers || [])?.map(({ id, offerName }) => ({
            label: offerName,
            value: id,
        }));
    }, [assignedTransactionOffers]);

    const unassigned = useMemo(() => {
        return availableTransactionOffers?.map(({ id, offerName }) => ({
            label: offerName,
            value: id,
        }));
    }, [availableTransactionOffers]);

    return (
        <OffersContainer title="Transactions Offers">
            {(fetchingActiveTransactionOffersForPartner || fetchingActiveTransactionOffers) && (
                <Loader />
            )}
            <AssignToggle
                assigned={assigned}
                unassigned={unassigned}
                assignedTitled="Assigned Offers"
                unassignedTitled="Available Offers"
                onAssign={async (transactionOfferIds) => {
                    const previousOffers = value || [];
                    setValue([...previousOffers, ...transactionOfferIds]);
                }}
                onUnassign={async (transactionOfferIds) => {
                    const previousOffers = value || [];
                    setValue(
                        previousOffers.filter(
                            ({ value }) =>
                                !transactionOfferIds.map(({ value }) => value).includes(value)
                        )
                    );
                }}
                onView={(offer: string | number) => {
                    if (availableTransactionOffers?.length > 0) {
                        const viewing = availableTransactionOffers.find(
                            (availableTransactionOffer: PartnerOffers) =>
                                availableTransactionOffer.id === offer
                        );
                        setSelectedOffer({
                            ...viewing,
                            name: viewing?.offerName,
                            type: PricingType.TRANSACTIONAL,
                        } as PartnerOffers);
                        setIsDialogOpen(availableTransactionOffers?.length > 0);
                    }
                }}
                containerProps={{ padding: '0px' }}
            />
            <OfferPreviewDialog
                isOpen={isDialogOpen}
                handleClose={() => {
                    setIsDialogOpen(!isDialogOpen);
                }}
                offer={selectedOffer}
            />
        </OffersContainer>
    );
};

export default TransactionsOffers;
